<app-confirm-dialog
  data-testid="om-entry-dialog"
  [visible]="true"
  [maxWidthRem]="40"
  [icon]="creating() ? 'add' : 'edit'"
  iconTon="akzent"
  confirmColor="primary"
  [title]="
    creating()
      ? ('objectMappings.entryDialog.titleCreate' | translate)
      : ('objectMappings.entryDialog.titleEdit' | translate: { name: entry()?.logicalName })
  "
  [message]="'objectMappings.entryDialog.message' | translate"
  [confirmLabel]="
    (creating() ? 'objectMappings.entryDialog.submitCreate' : 'objectMappings.entryDialog.submitEdit') | translate
  "
  [cancelLabel]="'objectMappings.entryDialog.cancel' | translate"
  [busy]="saving()"
  [confirmDisabled]="!canSave()"
  (confirmed)="save()"
  (cancelled)="cancelled.emit()"
>
  <form class="oed-form" data-testid="om-entry-form" (submit)="$event.preventDefault(); save()">
    <div class="oed-fields">
      <div class="oed-name">
        @if (creating()) {
          <mat-form-field
            appearance="fill"
            floatLabel="always"
            subscriptSizing="dynamic"
            class="oed-field tc-field tc-field--uppercase-label"
            [class.oed-field--error]="!!nameError()"
          >
            <mat-label>{{ 'objectMappings.entryDialog.name' | translate }}</mat-label>
            <input
              #nameInput
              matInput
              required
              class="oed-mono"
              data-testid="om-entry-name"
              autocomplete="off"
              [value]="name()"
              [placeholder]="'objectMappings.entryDialog.namePlaceholder' | translate: { example: nameExample() }"
              [attr.maxlength]="nameMax"
              [attr.aria-describedby]="nameError() ? 'om-entry-name-error' : 'om-entry-name-hint'"
              (input)="onName($event)"
              (blur)="onNameBlur()"
            />
          </mat-form-field>
          @if (nameError(); as problem) {
            <p id="om-entry-name-error" class="oed-hint oed-hint--error" data-testid="om-entry-name-error">
              {{ 'objectMappings.entryDialog.' + problem | translate }}
            </p>
          } @else if (prefixExample(); as recommendation) {
            <p id="om-entry-name-hint" class="oed-hint oed-hint--warn" data-testid="om-entry-name-hint">
              {{ 'objectMappings.entryDialog.prefixHint' | translate: recommendation }}
            </p>
          } @else {
            <p id="om-entry-name-hint" class="oed-hint" data-testid="om-entry-name-hint">
              {{ 'objectMappings.entryDialog.nameHint' | translate }}
            </p>
          }
        } @else {
          <div class="oed-readonly">
            <span class="oed-label">{{ 'objectMappings.entryDialog.name' | translate }}</span>
            <span class="oed-readonly-value" data-testid="om-entry-name-readonly">{{ entry()?.logicalName }}</span>
          </div>
          <p class="oed-hint" data-testid="om-entry-name-hint">
            {{ 'objectMappings.entryDialog.renameHint' | translate }}
          </p>
        }
        @if (libraryName()) {
          <p class="oed-hint" data-testid="om-entry-in-step">
            {{ 'objectMappings.entryDialog.inStep' | translate }}
            <code class="oed-code">{{ libraryName() }}.{{ stepName() }}</code>
          </p>
        }
      </div>

      <mat-form-field
        appearance="fill"
        floatLabel="always"
        subscriptSizing="dynamic"
        class="oed-field tc-field tc-field--uppercase-label"
      >
        <mat-label>{{ 'objectMappings.entryDialog.type' | translate }}</mat-label>
        <select matNativeControl required data-testid="om-entry-type" (change)="onType($event)">
          <option value="" [selected]="!typeName()">
            {{ 'objectMappings.entryDialog.typePlaceholder' | translate }}
          </option>
          @for (type of typeOptions(); track type) {
            <option [value]="type" [selected]="type === typeName()">{{ type }}</option>
          }
        </select>
      </mat-form-field>
    </div>

    <mat-form-field
      appearance="fill"
      floatLabel="always"
      subscriptSizing="dynamic"
      class="oed-field tc-field tc-field--uppercase-label"
    >
      <mat-label>{{ 'objectMappings.entryDialog.area' | translate }}</mat-label>
      <!-- Ohne maxlength: Laenger als 255 Zeichen meldet das BE (L5). -->
      <input
        matInput
        data-testid="om-entry-area"
        autocomplete="off"
        aria-describedby="om-entry-area-hint"
        [value]="area()"
        [placeholder]="'objectMappings.entryDialog.areaPlaceholder' | translate"
        (input)="onArea($event)"
      />
    </mat-form-field>
    <p id="om-entry-area-hint" class="oed-hint" data-testid="om-entry-area-hint">
      {{
        'objectMappings.entryDialog.areaHint'
          | translate: { technology: technologyLabelKey() ? (technologyLabelKey()! | translate) : engineId() }
      }}
    </p>

    <fieldset class="oed-candidates">
      <legend class="oed-label">{{ 'objectMappings.entryDialog.candidates' | translate }}</legend>
      <p class="oed-hint" data-testid="om-candidates-hint">
        {{
          'objectMappings.entryDialog.candidatesHint'
            | translate
              : {
                  first: kindOptions()[0].label | translate,
                  second: kindOptions()[1].label | translate,
                }
        }}
      </p>
      <ol class="oed-list">
        @for (row of rows(); track row.key; let i = $index, first = $first, last = $last) {
          <li class="oed-row" data-testid="om-locator-row">
            <span class="oed-rank" aria-hidden="true">{{ i + 1 }}</span>
            <mat-form-field appearance="fill" subscriptSizing="dynamic" class="oed-strategy tc-field">
              <select
                matNativeControl
                data-testid="om-locator-strategy"
                [attr.aria-label]="'objectMappings.entryDialog.strategy' | translate"
                (change)="onKind(i, $event)"
              >
                @for (option of rowOptions(row); track option.value) {
                  <option [value]="option.value" [selected]="option.value === row.choice">
                    {{ option.translated ? (option.label | translate) : option.label }}
                  </option>
                }
              </select>
            </mat-form-field>
            <mat-form-field appearance="fill" subscriptSizing="dynamic" class="oed-value tc-field">
              <input
                matInput
                required
                class="oed-mono"
                data-testid="om-locator-value"
                autocomplete="off"
                [value]="row.value"
                [placeholder]="'objectMappings.entryDialog.valuePlaceholder' | translate"
                [attr.aria-label]="'objectMappings.entryDialog.value' | translate"
                [attr.maxlength]="textMax"
                (input)="onValue(i, $event)"
              />
            </mat-form-field>
            <div class="oed-row-actions">
              <button
                type="button"
                class="oed-action"
                data-testid="om-locator-up"
                [disabled]="first"
                [attr.aria-label]="'objectMappings.entryDialog.moveUp' | translate: { position: i + 1 }"
                (click)="move(i, -1)"
              >
                <mat-icon aria-hidden="true">arrow_upward</mat-icon>
              </button>
              <button
                type="button"
                class="oed-action"
                data-testid="om-locator-down"
                [disabled]="last"
                [attr.aria-label]="'objectMappings.entryDialog.moveDown' | translate: { position: i + 1 }"
                (click)="move(i, 1)"
              >
                <mat-icon aria-hidden="true">arrow_downward</mat-icon>
              </button>
              <button
                type="button"
                class="oed-action oed-action--danger"
                data-testid="om-locator-remove"
                [disabled]="!canRemove()"
                [attr.aria-label]="'objectMappings.entryDialog.remove' | translate: { position: i + 1 }"
                (click)="removeRow(i)"
              >
                <mat-icon aria-hidden="true">delete</mat-icon>
              </button>
            </div>
          </li>
        }
      </ol>
      @if (fromFulltag()) {
        <p class="oed-hint" data-testid="om-locator-fulltag">
          {{ 'objectMappings.entryDialog.fromFulltag' | translate }}
        </p>
      } @else if (noCandidates()) {
        <p class="oed-hint oed-hint--error" data-testid="om-locator-empty">
          {{ 'objectMappings.entryDialog.candidatesRequired' | translate }}
        </p>
      }
      @if (showValueMissing()) {
        <p class="oed-hint oed-hint--error" data-testid="om-locator-value-missing">
          {{ 'objectMappings.entryDialog.valueRequired' | translate }}
        </p>
      }
      @if (showRoleHint()) {
        <p class="oed-hint oed-hint--error" data-testid="om-locator-role-hint">
          {{ 'objectMappings.entryDialog.roleHint' | translate }}
        </p>
      }
      @if (tooLong()) {
        <p class="oed-hint oed-hint--error" data-testid="om-locator-too-long">
          {{ 'objectMappings.entryDialog.tooLong' | translate }}
        </p>
      }
      <div class="oed-add">
        <button
          mat-stroked-button
          type="button"
          data-testid="om-locator-add"
          [disabled]="rows().length >= maxCandidates"
          (click)="addRow()"
        >
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'objectMappings.entryDialog.add' | translate }}
        </button>
        @if (rows().length >= maxCandidates) {
          <span class="oed-hint" data-testid="om-locator-max">
            {{ 'objectMappings.entryDialog.maxReached' | translate: { max: maxCandidates } }}
          </span>
        }
      </div>
    </fieldset>
  </form>
  @if (error(); as message) {
    <p class="oed-hint oed-hint--error" role="alert" data-testid="om-entry-error">{{ message }}</p>
  }
</app-confirm-dialog>
